import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import './EventFilters.css';
import * as myConstClass from '../../constant.js';
import shareIcon from '../../assets/icons/share.svg';
import { toast } from 'react-toastify';
import enTranslations from '../../en.json';
import maTranslations from '../../ma.json';

/**
 * Event Filters component
 * Allows users to filter events by category, topic, and language
 */
const EventFilters = ({ onFilterChange, initialFilters = {}, filterOptions = {} }) => {
    // Detect current language for translations (same pattern as TopBanner)
    const currentLanguage = localStorage.getItem('key') || 'en';
    const isMarathi = currentLanguage === 'ma';
    const lang = isMarathi ? maTranslations : enTranslations;

    // Helper to look up a key from the appropriate JSON translation file
    const t = (key) => lang[key] || enTranslations[key] || key;

    // Ensure only one event is selected initially (single selection like Topics)
    const initialEvents = initialFilters.events || ['Residential Study Courses'];
    
    // Map category names from URL to event type names used in the component
    const mapCategoryNameToEventName = (categoryName) => {
        if (!categoryName) return 'Residential Study Courses';
        
        // Normalize the category name (trim whitespace)
        const normalized = categoryName.trim();
        
        const nameMap = {
            'Residential Study Courses': 'Residential Study Courses',
            '1-Day Workshops': 'One Day Workshops',
            'One Day Workshops': 'One Day Workshops',
            'One Day Workshop': 'One Day Workshops',
            'Machine Tests': 'Machine Tests',
            'Machine Test': 'Machine Tests',
            'Rational Poojas': 'Rational Poojas',
            'Rational Pooja': 'Rational Poojas',
            'Rational Pooj': 'Rational Poojas', // Handle potential typos
            'Free Events': 'Free Events',
            'Free Event': 'Free Events',
            // Marathi name fallbacks (for backward compatibility with any old links)
            'निवासी अभ्यासवर्ग': 'Residential Study Courses',
            '१-दिवसीय कार्यशाळा': 'One Day Workshops',
            '१ दिवस कार्यशाळा': 'One Day Workshops',
            '१ दिवसीय कार्यशाळा': 'One Day Workshops',
            'मशीन चाचण्या': 'Machine Tests',
            'मशीन टेस्ट्स': 'Machine Tests',
            'बुद्धिनिष्ठ पूजा': 'Rational Poojas',
            'विनामूल्य उपक्रम': 'Free Events'
        };
        
        // Try exact match first
        if (nameMap[normalized]) {
            return nameMap[normalized];
        }
        
        // Try case-insensitive match
        const lowerNormalized = normalized.toLowerCase();
        for (const [key, value] of Object.entries(nameMap)) {
            if (key.toLowerCase() === lowerNormalized) {
                return value;
            }
        }
        
        // If no match found, return as-is (might match eventTypes directly)
        return normalized;
    };
    
    const [selectedEvents, setSelectedEvents] = useState(() => {
        if (Array.isArray(initialEvents) && initialEvents.length > 0) {
            const mappedName = mapCategoryNameToEventName(initialEvents[0]);
            return [mappedName];
        }
        return ['Residential Study Courses'];
    });
    const [selectedTopics, setSelectedTopics] = useState(initialFilters.topics || ['All']);
    const [selectedLanguage, setSelectedLanguage] = useState(initialFilters.language || 'All');
    const [selectedSubTopics, setSelectedSubTopics] = useState(initialFilters.subTopics || ['All']);
        const [selectedHasSchedule, setSelectedHasSchedule] = useState(() => {
        // Detect selected event type
        const initialEvent = Array.isArray(initialFilters.events) && initialFilters.events.length > 0
            ? initialFilters.events[0]
            : 'Residential Study Courses';

        const mappedEvent = mapCategoryNameToEventName(initialEvent);

        // Machine Tests → default All
        if (mappedEvent === 'Machine Tests') {
            return 'All';
        }

        // All other events → default Active
        if (initialFilters.hasSchedule === true) return 'Active';

        return 'Active';
    });

    const [availableSubtopics, setAvailableSubtopics] = useState([]);
    const [loadingSubtopics, setLoadingSubtopics] = useState(false);
    const [subtopicIds, setSubtopicIds] = useState(initialFilters.subtopicIds || []);
    
    // Accordion state for mobile view - track which sections are open
    const [openSections, setOpenSections] = useState({
        events: false,
        topics: false,
        language: false,
        subtopics: false
    });
    
    // Toggle accordion section - close others when opening a new one
    const toggleSection = (sectionName) => {
        setOpenSections(prev => {
            const isCurrentlyOpen = prev[sectionName];
            
            // If clicking the same section, toggle it
            // If opening a new section, close all others and open only this one
            if (isCurrentlyOpen) {
                // Close the section that's being clicked
                return {
                    ...prev,
                    [sectionName]: false
                };
            } else {
                // Close all sections and open only the clicked one
                return {
                    events: false,
                    topics: false,
                    language: false,
                    subtopics: false,
                    [sectionName]: true
                };
            }
        });
    };

    const eventTypes = [
        { id: 1, name: 'Residential Study Courses', label: t('residentialsc') },
        { id: 2, name: 'One Day Workshops', label: t('1day') },
        { id: 3, name: 'Machine Tests', label: t('machine_tests') },
        { id: 4, name: 'Rational Poojas', label: t('rational_poojas') },
        { id: 5, name: 'Free Events', label: t('free_events') }
    ];
    
    // Update state when initialFilters prop changes (e.g., when navigating between categories)
    useEffect(() => {
        if (initialFilters.events && Array.isArray(initialFilters.events) && initialFilters.events.length > 0) {
            const categoryNameFromUrl = initialFilters.events[0];
            const mappedName = mapCategoryNameToEventName(categoryNameFromUrl);
            setSelectedEvents(prevEvents => {
                // Only update if different to avoid unnecessary re-renders
                const currentSelected = prevEvents[0];
                if (currentSelected !== mappedName) {
                    return [mappedName];
                }
                return prevEvents;
            });
        } else if (!initialFilters.events || (Array.isArray(initialFilters.events) && initialFilters.events.length === 0)) {
            // If no events in initialFilters, ensure we have a default
            setSelectedEvents(prevEvents => {
                if (prevEvents.length === 0 || !prevEvents[0]) {
                    return ['Residential Study Courses'];
                }
                return prevEvents;
            });
        }
        // Handle topics - update even if it's an empty array or 'All'
        if (initialFilters.topics !== undefined && Array.isArray(initialFilters.topics)) {
            // Normalize topic names to match with filterOptions topics
            const normalizeTopicName = (topicName) => {
                if (!topicName || topicName === 'All') return topicName;
                
                // Get available topics from filterOptions
                if (filterOptions.topics && filterOptions.topics.length > 0) {
                    // Try to find matching topic (case-insensitive, trim whitespace)
                    const normalizedInput = topicName.trim().toLowerCase();
                    const matchingTopic = filterOptions.topics.find(t => {
                        const topicNameVariants = [
                            t.name,
                            t.nameEn,
                            t.nameMr,
                            t.title,
                            t.titleEn,
                            t.titleMr,
                            t.topic_title,
                            t.topic_title_en,
                            t.topic_title_mr,
                            t.label
                        ];
                        return topicNameVariants.some(variant => 
                            variant && variant.toString().trim().toLowerCase() === normalizedInput
                        );
                    });
                    
                    if (matchingTopic) {
                        // Return the name that EventFilters uses (prefer nameEn or name)
                        return matchingTopic.nameEn || matchingTopic.name || matchingTopic.titleEn || matchingTopic.title || topicName;
                    }
                }
                
                // If no match found, return original topic name
                return topicName;
            };
            
            const normalizedTopics = initialFilters.topics.length > 0 
                ? initialFilters.topics.map(normalizeTopicName)
                : ['All'];
            
            // Only update if different to avoid unnecessary re-renders
            setSelectedTopics(prevTopics => {
                const prevTopicsStr = prevTopics.join(',');
                const newTopicsStr = normalizedTopics.join(',');
                if (prevTopicsStr !== newTopicsStr) {
                    return normalizedTopics;
                }
                return prevTopics;
            });
        }
        if (initialFilters.language) {
            setSelectedLanguage(initialFilters.language);
        }
        if (initialFilters.subTopics) {
            setSelectedSubTopics(initialFilters.subTopics);
        }
           if (initialFilters.hasSchedule !== undefined) {
            if (initialFilters.hasSchedule === true) {
                setSelectedHasSchedule('Active');
            } else {
                setSelectedHasSchedule('All');
            }
        } else if (initialFilters.events && initialFilters.events.length > 0) {
            const mappedEvent = mapCategoryNameToEventName(initialFilters.events[0]);
            if (mappedEvent === 'Machine Tests') {
                setSelectedHasSchedule('All');
            } else {
                setSelectedHasSchedule('Active');
            }
        }
    }, [
        initialFilters.events ? initialFilters.events.join(',') : '', 
        initialFilters.topics ? initialFilters.topics.join(',') : '', 
        initialFilters.language, 
        initialFilters.subTopics ? initialFilters.subTopics.join(',') : '',
        // Re-run normalization when available topics from the API change
        filterOptions.topics
    ]);

    // Use dynamic topics from API if available, otherwise use fallback
    const getTopics = () => {

        if (filterOptions.topics && filterOptions.topics.length > 0) {
            // Transform API topics to component format
            const apiTopics = filterOptions.topics.map((topic, index) => {
                // Try to get the English key for translation lookup
                const topicKey = topic.nameEn || topic.name || topic.titleEn || topic.title || topic.label || '';
                // Prefer Marathi label if available and language is Marathi
                let label = t(topicKey);
                if (isMarathi && (topic.nameMr || topic.titleMr || topic.labelMr)) {
                    label = topic.nameMr || topic.titleMr || topic.labelMr;
                }
                return {
                    id: topic.id || topic.topic_id || `topic-${index}`,
                    name: topicKey,
                    label
                };
            }).filter(topic => topic.name && topic.name !== ''); // Filter out empty topics

            // Add "All" option at the beginning
            return [
                { id: 'all', name: 'All', label: t('filter_all') },
                ...apiTopics
            ];
        }

        // Fallback to hardcoded topics if API topics not available
        return [
            { id: 'all', name: 'All', label: t('filter_all') },
            { id: 'prenatal', name: 'Prenatal', label: t('Prenatal') },
            { id: 'kids', name: 'Kids 8-14', label: t('Kids 8-14') },
            { id: 'youth', name: 'Youth 14-28', label: t('Youth 14-28') },
            { id: 'adult', name: 'Adult/General', label: t('Adult/General') }
        ];
    };
    
    const topics = getTopics();

    const handleTopicChange = (topicName) => {
        setSelectedTopics(prevTopics => {
            let updatedTopics;
            if (topicName === 'All') {
                updatedTopics = ['All'];
            } else {
                if (prevTopics.includes('All')) {
                    updatedTopics = [topicName];
                } else {
                    if (prevTopics.includes(topicName)) {
                        updatedTopics = prevTopics.filter(t => t !== topicName);
                        if (updatedTopics.length === 0) updatedTopics = ['All'];
                    } else {
                        updatedTopics = [...prevTopics, topicName];
                    }
                }
            }

            // Reset subtopics selection when topics change
            setSelectedSubTopics(['All']);
            setSubtopicIds([]);

            notifyFilterChange({
                events: selectedEvents,
                topics: updatedTopics,
                language: selectedLanguage,
                subTopics: ['All'],
                subtopicIds: [],
                hasSchedule: getHasScheduleValue(selectedHasSchedule)
            });

            return updatedTopics;
        });
    };

    // Use numeric IDs for language for easier mapping
    const languages = [
        { id: 0, name: 'All', label: t('filter_all') },
        { id: 1, name: 'English', label: t('filter_lang_english') },
        { id: 2, name: 'Marathi', label: t('filter_lang_marathi') },
        { id: 3, name: 'Hindi', label: t('filter_lang_hindi') }
    ];

    // Helper to get language ID from name
    const getLanguageIdByName = (langName) => {
        const lang = languages.find(l => l.name.toLowerCase() === String(langName).toLowerCase());
        return lang ? lang.id : 0;
    };

    const hasScheduleOptions = [
        { id: 'all', name: 'All', label: t('filter_all') },
        { id: 'active', name: 'Active', label: t('filter_active') }
    ];

    // Handle sharing filters
    const handleShare = async (e) => {
        if (e && e.stopPropagation) e.stopPropagation();
        try {
            const origin = (typeof window !== 'undefined' && window.location && window.location.origin) ? window.location.origin : '';
            const queryParts = [];

            // Resolve event type ID
            const eventType = selectedEvents && selectedEvents.length > 0 ? selectedEvents[0] : 'Events Information';
            let eventTypeId = null;
            const foundLocal = eventTypes.find(e => e.name === eventType || e.label === eventType);
            if (foundLocal) {
                eventTypeId = foundLocal.id;
            } else if (filterOptions && filterOptions.eventTypes && filterOptions.eventTypes.length > 0) {
                const foundApi = filterOptions.eventTypes.find(et => {
                    const variants = [et.name, et.nameEn, et.nameMr, et.label, et.title, et.titleEn];
                    return variants.some(v => v && v.toString().trim().toLowerCase() === eventType.trim().toLowerCase());
                });
                if (foundApi) {
                    eventTypeId = foundApi.id || foundApi.value || foundApi.event_type_id || foundApi.eventTypeId;
                }
            }
            if (!eventTypeId) {
                eventTypeId = 1;
            }
            if (eventTypeId) {
                queryParts.push(`event_type_id=${encodeURIComponent(eventTypeId)}`);
            }

            // Get topic IDs for selected topics
            const getTopicIds = () => {
                if (selectedTopics.includes('All') || selectedTopics.length === 0) {
                    return [];
                }
                const topicIds = selectedTopics
                    .map(topicName => {
                        if (!filterOptions.topics || filterOptions.topics.length === 0) {
                            return null;
                        }
                        const normalizedTopicName = topicName.trim().toLowerCase();
                        const topic = filterOptions.topics.find(t => {
                            const possibleNames = [
                                t.name,
                                t.nameEn,
                                t.nameMr,
                                t.label,
                                t.title,
                                t.titleEn,
                                t.titleMr,
                                t.topic_title,
                                t.topic_title_en,
                                t.topic_title_mr
                            ];
                            return possibleNames.some(fieldValue => 
                                fieldValue && fieldValue.toString().trim().toLowerCase() === normalizedTopicName
                            );
                        });
                        if (topic) {
                            return topic.id || topic.topic_id || topic.topicId;
                        }
                        return null;
                    })
                    .filter(id => id !== null && id !== undefined);
                return topicIds;
            };
            const topicIds = getTopicIds();
            if (topicIds.length > 0) {
                queryParts.push(`topic_id=${topicIds.join(',')}`);
            }

            // Add language_id instead of language
            if (selectedLanguage && selectedLanguage !== 'All') {
                const langId = getLanguageIdByName(selectedLanguage);
                queryParts.push(`language_id=${langId}`);
            }

            // Add subtopic IDs (not names)
            if (subtopicIds && subtopicIds.length > 0) {
                queryParts.push(`subtopic_id=${subtopicIds.join(',')}`);
            }

            // Build the shareable URL
            const baseUrl = `${origin}/booknow`;
            const shareUrl = queryParts.length > 0 ? `${baseUrl}?${queryParts.join('&')}` : baseUrl;

            // Compose the share text (do NOT include the URL here for native share)
            const shareText = `Do look at the following ${eventType} at Manashakti Research Centre which I\nfelt might be relevant for you. Manashakti Research Centre is a non profit\nthat works in holistic education and health.`;

            // Try native share API first
            if (typeof navigator !== 'undefined' && navigator.share) {
                await navigator.share({
                    title: 'Manashakti Events',
                    text: shareText,
                    url: shareUrl
                });
                return true;
            }

            // Fallback: copy to clipboard (append URL to text)
            if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
                toast.success('Link copied to clipboard!');
                return true;
            }

            // Final fallback: prompt (append URL to text)
            window.prompt('Copy this link:', `${shareText}\n${shareUrl}`);
            return true;
        } catch (err) {
            console.error('Share failed', err);
            if (err.name !== 'AbortError') {
                toast.error('Failed to share link');
            }
            return false;
        }
    };

    // Fetch subtopics when topics are selected
    useEffect(() => {
        const fetchSubtopics = async () => {
            // If no topics selected or "All" is selected, clear subtopics
            if (selectedTopics.length === 0 || selectedTopics.includes('All')) {
                setAvailableSubtopics([]);
                setSelectedSubTopics(['All']); // Clear selected subtopics
                return;
            }

            // Get topic IDs from selected topic names
            const getTopicIds = () => {
                if (filterOptions.topics && filterOptions.topics.length > 0) {
                    return selectedTopics
                        .map(topicName => {
                            // Normalize topic name for matching (case-insensitive, trim whitespace)
                            const normalizedInput = topicName.trim().toLowerCase();
                            const topic = filterOptions.topics.find(t => {
                                const topicNameVariants = [
                                    t.name,
                                    t.nameEn,
                                    t.nameMr,
                                    t.title,
                                    t.titleEn,
                                    t.titleMr,
                                    t.topic_title,
                                    t.topic_title_en,
                                    t.topic_title_mr,
                                    t.label
                                ];
                                return topicNameVariants.some(variant => 
                                    variant && variant.toString().trim().toLowerCase() === normalizedInput
                                );
                            });
                            return topic ? (topic.id || topic.topic_id) : null;
                        })
                        .filter(id => id !== null && id !== undefined);
                }
                return [];
            };

            const topicIds = getTopicIds();

            if (topicIds.length === 0) {
                setAvailableSubtopics([]);
                return;
            }

            try {
                setLoadingSubtopics(true);
                const response = await fetch(`${myConstClass.NODE_API_BASE}/topics/subtopics?language=${currentLanguage}`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        topic_ids: topicIds
                    }),
                });

                const data = await response.json();

                if (data.status === 'true' && data.data) {
                    // Transform API subtopics to component format
                    const transformedSubtopics = data.data.map((subtopic) => ({
                        id: subtopic.value || subtopic.id,
                        name: subtopic.label || subtopic.name || '',
                        label: subtopic.label || subtopic.name || ''
                    }));

                    setAvailableSubtopics(transformedSubtopics);
                    // Preserve initial subtopic selection if it exists and is valid
                    // Check if we have a selected subtopic from initialFilters that matches available subtopics
                    const currentSelected = selectedSubTopics.filter(st => st !== 'All');
                    if (currentSelected.length > 0) {
                        // Check if any of the currently selected subtopics exist in the newly loaded subtopics
                        const validSelected = currentSelected.filter(selectedName => {
                            return transformedSubtopics.some(st => 
                                (st.name && st.name === selectedName) || 
                                (st.label && st.label === selectedName)
                            );
                        });
                        // If we have valid selected subtopics, keep them; otherwise clear
                        if (validSelected.length > 0) {
                            setSelectedSubTopics(validSelected);
                        } else {
                            setSelectedSubTopics(['All']);
                        }
                    } else {
                        // Clear selected subtopics when topics change and no valid selection exists
                        setSelectedSubTopics(['All']);
                    }
                } else {
                    setAvailableSubtopics([]);
                }
            } catch (error) {
                console.error('Error fetching subtopics:', error);
                setAvailableSubtopics([]);
            } finally {
                setLoadingSubtopics(false);
            }
        };

        fetchSubtopics();
    }, [selectedTopics, filterOptions.topics, currentLanguage]);

    // Auto-select subtopics from URL (deep link support)
    useEffect(() => {
        // Only proceed if:
        // 1. We have subtopics loaded from API
        // 2. We have subtopic IDs from URL (initialFilters)
        if (availableSubtopics.length > 0 && 
            initialFilters.subtopicIds && 
            initialFilters.subtopicIds.length > 0) {
            
            // Match subtopic IDs from URL with loaded subtopics
            const matchedSubtopics = [];
            const matchedIds = [];
            
            initialFilters.subtopicIds.forEach(urlSubtopicId => {
                const found = availableSubtopics.find(st => {
                    const stId = st.id || st.value;
                    return stId && parseInt(stId, 10) === parseInt(urlSubtopicId, 10);
                });
                
                if (found) {
                    const subtopicName = found.name || found.label;
                    matchedSubtopics.push(subtopicName);
                    matchedIds.push(found.id || found.value);
                }
            });
            
            // Update selectedSubTopics if we found matches
            if (matchedSubtopics.length > 0) {
                setSelectedSubTopics(matchedSubtopics);
                setSubtopicIds(matchedIds);
                
                // Notify parent with the auto-selected subtopics
                notifyFilterChange({ 
                    events: selectedEvents, 
                    topics: selectedTopics, 
                    language: selectedLanguage, 
                    subTopics: matchedSubtopics,
                    subtopicIds: matchedIds,
                    hasSchedule: getHasScheduleValue(selectedHasSchedule)
                });
            }
        }
    }, [availableSubtopics]); // Only trigger when subtopics are loaded

    // Use dynamic subtopics only when a topic (not 'All') is selected. Return empty when 'All' is selected.
    const getSubTopics = () => {
        // If no specific topic selected or 'All' is selected, return empty list so the message is shown instead
        if (!selectedTopics || selectedTopics.length === 0 || selectedTopics.includes('All')) {
            return [];
        }

        // If API-provided subtopics are available, include the 'All' option
        if (availableSubtopics && availableSubtopics.length > 0) {
            return [
                { id: 'all', name: 'All', label: t('filter_all') },
                ...availableSubtopics
            ];
        }

        // No API subtopics found for the selected topics
        return [];
    };
    
    const subTopics = getSubTopics();

    const handleEventChange = (eventName) => {
        // Only allow one event to be selected at a time (like Topics filter)
        let updatedEvents;
        if (selectedEvents.includes(eventName)) {
            // If clicking the already selected event, keep it selected (don't deselect)
            updatedEvents = [eventName];
        } else {
            // Select only this event (replace previous selection)
            updatedEvents = [eventName];
        }
        setSelectedEvents(updatedEvents);
        notifyFilterChange({ events: updatedEvents, topics: selectedTopics, language: selectedLanguage, hasSchedule: getHasScheduleValue(selectedHasSchedule) });
    };

    const handleLanguageChange = (languageName) => {
        setSelectedLanguage(languageName);
        notifyFilterChange({ events: selectedEvents, topics: selectedTopics, language: languageName, subTopics: selectedSubTopics, hasSchedule: getHasScheduleValue(selectedHasSchedule) });
    };

    const getHasScheduleValue = (value) => {
        if (!value || value === 'All') return undefined;
        return value === 'Active' ? true : undefined;
    };

    const handleHasScheduleChange = (value) => {
        setSelectedHasSchedule(value);
        const hasScheduleValue = getHasScheduleValue(value);
        notifyFilterChange({ 
            events: selectedEvents, 
            topics: selectedTopics, 
            language: selectedLanguage, 
            subTopics: selectedSubTopics,
            hasSchedule: hasScheduleValue
        });
    };

    const handleSubTopicChange = (subTopicName) => {
        // Use functional state update to ensure we work with latest state
        setSelectedSubTopics(prevSubTopics => {
            let updatedSubTopics;
            
            if (subTopicName === 'All') {
                updatedSubTopics = ['All'];
            } else {
                if (prevSubTopics.includes('All')) {
                    updatedSubTopics = [subTopicName];
                } else {
                    if (prevSubTopics.includes(subTopicName)) {
                        updatedSubTopics = prevSubTopics.filter(st => st !== subTopicName);
                        if (updatedSubTopics.length === 0) {
                            updatedSubTopics = ['All'];
                        }
                    } else {
                        updatedSubTopics = [...prevSubTopics, subTopicName];
                    }
                }
            }
            
            // Get subtopic IDs for the selected subtopics
            const getSubtopicIds = () => {
                if (updatedSubTopics.includes('All') || updatedSubTopics.length === 0) {
                    return [];
                }
                
                return updatedSubTopics
                    .map(subtopicName => {
                        const subtopic = availableSubtopics.find(st => 
                            st.name === subtopicName || st.label === subtopicName
                        );
                        return subtopic ? (subtopic.id || subtopic.value) : null;
                    })
                    .filter(id => id !== null && id !== undefined);
            };
            
            const updatedSubtopicIds = getSubtopicIds();
            
            // Update subtopic IDs state
            setSubtopicIds(updatedSubtopicIds);
            
            // Notify parent with updated subTopics (both names and IDs)
            notifyFilterChange({ 
                events: selectedEvents, 
                topics: selectedTopics, 
                language: selectedLanguage, 
                subTopics: updatedSubTopics,
                subtopicIds: updatedSubtopicIds, // Pass IDs for API call
                hasSchedule: getHasScheduleValue(selectedHasSchedule)
            });
            
            return updatedSubTopics;
        });
    };

    const notifyFilterChange = (filters) => {
        if (onFilterChange) {
            onFilterChange(filters);
        }
    };

    return (
        <div className="event-filters">
            <div className="event-filters__header">
                <h3 className="event-filters__title">{t('filter_title')}</h3>
                <button
                    type="button"
                    onClick={handleShare}
                    aria-label={t('filter_share_aria')}
                    title={t('filter_share_title')}
                    className="event-filters__share-btn"
                >
                    <img src={shareIcon} alt="Share" className="event-filters__share-icon" />
                </button>
            </div>

            {/* Events Filter */}
            <div className="event-filters__section">
                <button 
                    className="event-filters__accordion-header"
                    onClick={() => toggleSection('events')}
                    aria-expanded={openSections.events}
                >
                    <h4 className="event-filters__section-title">{t('filter_type_label')}</h4>
                    <i className={`fas fa-chevron-${openSections.events ? 'up' : 'down'} event-filters__chevron`} aria-hidden="true"></i>
                </button>
                <div className={`event-filters__options--accordion ${openSections.events ? 'event-filters__options--open' : ''}`}>
                    <div className="event-filters__type-label">{t('filter_type_label')}</div>
                    <div className="event-filters__options">
                    {eventTypes.map((event) => {
                        const isChecked = selectedEvents.includes(event.name);
                    
                        return (
                            <label key={event.id} className="event-filters__checkbox-label">
                                <input
                                    type="checkbox"
                                    className="event-filters__checkbox"
                                    checked={isChecked}
                                    onChange={() => handleEventChange(event.name)}
                                />
                                <span className="event-filters__checkbox-text event-type">{event.label}</span>
                            </label>
                        );
                    })}
                    </div>
                </div>
            </div>

            {/* Topics Filter */}
            <div className="event-filters__section">
                <button 
                    className="event-filters__accordion-header"
                    onClick={() => toggleSection('topics')}
                    aria-expanded={openSections.topics}
                >
                    <h4 className="event-filters__section-title">{t('filter_topics_section')}</h4>
                    <i className={`fas fa-chevron-${openSections.topics ? 'up' : 'down'} event-filters__chevron`} aria-hidden="true"></i>
                </button>
                <div className={`event-filters__options--accordion ${openSections.topics ? 'event-filters__options--open' : ''}`}>
                    <div className="event-filters__type-label">{t('filter_topics_section')}</div>
                    <div className="event-filters__options">
                    {topics.map((topic) => (
                        <label key={topic.id} className="event-filters__checkbox-label">
                            <input
                                type="checkbox"
                                className="event-filters__checkbox"
                                checked={selectedTopics.includes(topic.name)}
                                onChange={() => handleTopicChange(topic.name)}
                            />
                            <span className="event-filters__checkbox-text">{topic.label}</span>
                        </label>
                    ))}
                    </div>
                </div>
            </div>

            {/* Language Filter */}
            <div className="event-filters__section">
                <button 
                    className="event-filters__accordion-header"
                    onClick={() => toggleSection('language')}
                    aria-expanded={openSections.language}
                >
                    <h4 className="event-filters__section-title">{t('filter_language_section')}</h4>
                    <i className={`fas fa-chevron-${openSections.language ? 'up' : 'down'} event-filters__chevron`} aria-hidden="true"></i>
                </button>
                <div className={`event-filters__language-buttons event-filters__options--accordion ${openSections.language ? 'event-filters__options--open' : ''}`}>
                    <div className="event-filters__type-label">{t('filter_language_section')}</div>
                    <div className="event-filters__language-row">
                    {languages.map((lang) => (
                        <button
                            key={lang.id}
                            className={`event-filters__language-btn ${
                                selectedLanguage === lang.name ? 'event-filters__language-btn--active' : ''
                            }`}
                            onClick={() => handleLanguageChange(lang.name)}
                        >
                            {lang.label}
                        </button>
                    ))}
                    </div>
                </div>
            </div>

            {/* Active / Inactive (hasSchedule) Filter */}
            <div className="event-filters__section">
                <button 
                    className="event-filters__accordion-header"
                    onClick={() => toggleSection('hasSchedule')}
                    aria-expanded={openSections.hasSchedule}
                >
                    <h4 className="event-filters__section-title">{t('filter_status_section')}</h4>
                    <i className={`fas fa-chevron-${openSections.hasSchedule ? 'up' : 'down'} event-filters__chevron`} aria-hidden="true"></i>
                </button>
                <div className={`event-filters__language-buttons event-filters__options--accordion ${openSections.hasSchedule ? 'event-filters__options--open' : ''}`}>
                    <div className="event-filters__type-label">{t('filter_status_section')}</div>
                    <div className="event-filters__language-row">
                    {hasScheduleOptions.map((opt) => (
                        <button
                            key={opt.id}
                            className={`event-filters__language-btn ${
                                selectedHasSchedule === opt.name ? 'event-filters__language-btn--active' : ''
                            }`}
                            onClick={() => handleHasScheduleChange(opt.name)}
                        >
                            {opt.label}
                        </button>
                    ))}
                    </div>
                </div>
            </div>

            {/* Sub-Topics Filter */}
            <div className="event-filters__section">
                <button 
                    className="event-filters__accordion-header"
                    onClick={() => toggleSection('subtopics')}
                    aria-expanded={openSections.subtopics}
                >
                    <h4 className="event-filters__section-title">{t('filter_subtopics_section')}</h4>
                    <i className={`fas fa-chevron-${openSections.subtopics ? 'up' : 'down'} event-filters__chevron`} aria-hidden="true"></i>
                </button>
                <div className={`event-filters__options--accordion ${openSections.subtopics ? 'event-filters__options--open' : ''}`}>
                    {loadingSubtopics && selectedTopics.length > 0 && !selectedTopics.includes('All') && (
                        <p className="event-filters__hint">{t('filter_loading_subtopics')}</p>
                    )}
                    {!loadingSubtopics && selectedTopics.length > 0 && !selectedTopics.includes('All') && availableSubtopics.length === 0 && (
                        <p className="event-filters__hint">{t('filter_no_subtopics')}</p>
                    )}
                    {selectedTopics.includes('All') && (
                        <p className="event-filters__hint">{t('filter_select_topics_hint')}</p>
                    )}
                    <div className="event-filters__type-label">{t('filter_subtopics_section')}</div>
                    <div className="event-filters__options">
                    {!loadingSubtopics && subTopics.length > 1 && subTopics.map((subTopic) => (
                        <label key={subTopic.id} className="event-filters__checkbox-label">
                            <input
                                type="checkbox"
                                className="event-filters__checkbox"
                                checked={selectedSubTopics.includes(subTopic.name)}
                                onChange={() => handleSubTopicChange(subTopic.name)}
                            />
                            <span className="event-filters__checkbox-text">{subTopic.label}</span>
                        </label>
                    ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

EventFilters.propTypes = {
    onFilterChange: PropTypes.func,
    initialFilters: PropTypes.shape({
        events: PropTypes.arrayOf(PropTypes.string),
        topics: PropTypes.arrayOf(PropTypes.string),
        language: PropTypes.string,
        subTopics: PropTypes.arrayOf(PropTypes.string),
        hasSchedule: PropTypes.bool
    }),
    filterOptions: PropTypes.shape({
        eventTypes: PropTypes.array,
        topics: PropTypes.array,
        ageGroups: PropTypes.array,
        cities: PropTypes.array,
        states: PropTypes.array,
        countries: PropTypes.array,
        trusts: PropTypes.array
    })
};

export default EventFilters;

